import styled, { css } from 'styled-components'
import vars from '@/assets/styles/vars';
import { maxHeight, maxWidth } from '@/assets/styles/mixins.css.js'

const scrollStyles = () => css`
  scrollbar-color: var(--color-extra) #ffffff;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    background-color: #ffffff;
    width: 2px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--color-extra);
  }
`

const directionsListWidth = 300

const Nav = styled.nav`
  position: relative;
  width: 100%;
  padding: 0 10px 0 245px;
`

Nav.Item = styled.li`
  ${props =>
    props.type === 'small' &&
    css`
      position: relative;

      ${Nav.Dropdown} {
        width: 240px;
        top: calc(100% + 20px);
      }

      ${Nav.DropdownInner} {
        display: block;
        height: auto;
      }
    `}
`

Nav.List = styled.ul`
  display: flex;

  ${({ listType }) => {
    switch (listType) {
      case 'directions':
        return css`
          display: block;
          flex-shrink: 0;
          width: ${directionsListWidth}px;
          height: 100%;
          max-height: 100%;
          overflow-y: auto;
          background: var(--color-light);
          box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.04);
          pointer-events: all;
          
          ${scrollStyles}

          & > ${Nav.Item} {
            &:first-child {
              padding-top: 24px;
            }

            &:last-child {
              padding-bottom: 24px;
            }

            & > ${Nav.Link} {
              display: inline-block;
              width: 100%;
              padding: 10px 60px 10px 22px;
              transition: color 0.3s ease-out, text-shadow 0.3s ease-out;
              pointer-events: all;

              &:not(:only-child) {
                & > span {
                  position: relative;
                  display: inline-block;

                  &::after {
                    content: '';
                    position: absolute;
                    left: calc(100% + 16px);
                    top: 2px;
                    display: block;
                    width: 23px;
                    height: 14px;
                    opacity: 0;
                    background-image: url("data:image/svg+xml,%3Csvg width='24' height='14' viewBox='0 0 24 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 8.4h16.333V14l7-7-7-7v5.6H0v2.8z' fill='%23b69b64'/%3E%3C/svg%3E");
                    background-size: 100% 100%;
                    background-repeat: no-repeat;
                    transform-origin: 50% 50%;
                    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
                  }
                }
              }
            }

            &:hover > ${Nav.Link} {
              color: var(--color-decor);
              text-shadow: 0.5px 0 0 currentColor;
              transition: color 0.3s 0.15s ease-out, text-shadow 0.3s 0.15s ease-out;

              & > span::after {
                opacity: 1;
                transform: translateX(4px);
                transition: transform 0.3s 0.15s ease-in-out, opacity 0.3s 0.15s ease-in-out;
              }
            }
          }
        `
      case 'childs':
        return css` 
          flex-direction: column;
          align-items: flex-start;
          flex-wrap: wrap;
          column-gap: 20px;
          overflow-y: auto;
          height: 100%;
          padding: 12px;
          pointer-events: all;

          ${scrollStyles}

          & > ${Nav.Item} {
            min-width: 50%;

            & > ${Nav.Link} {
              padding: 10px 10px;
              font-size: 14px;
              transition: color 0.3s ease-out;

              &:hover {
                color: var(--color-decor);
              }

              &:not(:only-child) {
                position: relative;
                padding-bottom: 6px;

                &::after {
                  content: '';
                  position: absolute;
                  left: -5px;
                  top: 15px;
                  display: block;
                  width: 7px;
                  height: 7px;
                  background-image: url("data:image/svg+xml,%3Csvg width='7' height='7' viewBox='0 0 7 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.24266 2.87132L3.37134 5.74265L0.500013 2.87132' stroke='%23B69B64' /%3E%3C/svg%3E");
                  background-size: 100% 100%;
                  background-repeat: no-repeat;
                  transform-origin: 50% 50%;
                  transition: transform 0.3s ease;
                }
              }
            }
          }
        `
      case 'grandchilds':
        return css`
          display: block;
          padding-bottom: 8px;
          pointer-events: all;

          & > ${Nav.Item} {
            & > ${Nav.Link} {
              padding: 6px 10px 7px;
              font-size: 14px;
              color: var(--color-secondary);
              transition: color 0.3s ease-out;

              &:hover {
                color: var(--color-decor);
              }
            }
          }
        `
      case 'pages':
        return css`
          display: block;
          pointer-events: all;
          background: var(--color-light);
          box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.04);

          & > ${Nav.Item} {
            &:first-child > ${Nav.Link} {
              padding-top: 28px;
            }

            &:last-child > ${Nav.Link} {
              padding-bottom: 28px;
            }

            & > ${Nav.Link} {
              padding: 10px 22px;
              font-size: 14px;
              transition: color 0.3s ease-out;

              &:hover {
                color: var(--color-decor);
              }
            }
          }
        `
      default:
        return css`
          position:relative;
          column-gap: min(50px, 3.4vw);
          align-items: center;
          justify-content:center;
          padding-bottom: 20px;
          width: fit-content;
          margin: 0 auto;

           ${maxWidth(1060)`
            column-gap: 25px;
          `}
          
          & > ${Nav.Item} {

            &:hover > ${Nav.Link} {
              &::before {
                transform: none;
                transition: transform 0.2s 0.15s linear;
              }
            }

            & > ${Nav.Link} {
              display: flex;
              align-items: center;
              gap: 7px;
              text-transform: uppercase;
              &::before {
                content: '';
                position: absolute;
                bottom: -1px;
                left: 0;
                z-index: 1;
                width: 100%;
                height: 1px;
                background: #b69b64;;
                transform: scale(0);
                transform-origin: 50% 100%;
                transition: transform 0.2s linear;
              }

              &:not(:only-child) {
               &::before {
               width: calc(100% - 14px);
                }
                &::after {
                  content: '';
                  display: inline-block;
                  width: 7px;
                  height: 7px;
                  background-image: url("data:image/svg+xml,%3Csvg width='7' height='7' viewBox='0 0 7 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.24266 2.87132L3.37134 5.74265L0.500013 2.87132' stroke='%23B69B64' /%3E%3C/svg%3E");                  background-size: 100% 100%;
                  background-repeat: no-repeat;
                  transform-origin: 50% 50%;
                  transition: transform 0.3s ease;
                  flex-shrink:0;
                }
              }
            }

            &:hover {
              & > ${Nav.Link} {
                &::after {
                  transform: rotate(180deg);
                  transition: transform 0.3s 0.15s ease-in;
                }
              }
            }
          }
        `
    }
  }}
`

Nav.Link = styled.a`
  position: relative;
  display:block;
  font-size: 14px;
  line-height: 17px;
  color: var(--color-decor);

  ${props =>
    props.active &&
    css`
      cursor: default;
    `}
`

Nav.Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1;
  width: 100%;
  border-top: 2px solid transparent;
  background: 0 0;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: transform 0.2s linear, opacity 0.2s linear, visibility 0s 0.2s;

  ${props =>
    props.visible &&
    css`
      opacity: 1;
      visibility: visible;
      transform: none;
      transition: transform 0.3s 0.15s ease-out, opacity 0.3s 0.15s ease-out, visibility 0s 0.15s !important;
    `}

  & & {
    top: 0;
    left: ${directionsListWidth}px;
    background: 0 0;
    border-top: none;
    height: 100%;
    width: calc(100% - ${directionsListWidth}px);
    transform: none;
    transition: opacity 0.3s linear, visibility 0s 0.3s;
  }
`

Nav.DropdownInner = styled.div`
  display: flex;
  width: 100%;
  height: 480px;

  ${maxHeight(700)`
    height: 400px;
  `}
`

Nav.DropdownBg = styled.div`
  width: calc(100% - ${directionsListWidth}px);
  height: 100%;
  opacity: 0;
  transition: opacity 0.25s 0.15s ease-in;
  background-color: var(--color-bg-accent);
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTE3NCIgaGVpZ2h0PSI1OTIiIHZpZXdCb3g9IjAgMzIgMTE3NCA1OTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsdGVyPSJ1cmwoI2ZpbHRlcjFfZGQpIj48cGF0aCBkPSJNMzM1LjU1NiA0MDMuNzE1QzIwMS40NjIgMzM3LjMzNiAzMiAzNDcuNzg3IDMyIDMyaDU4NC4yMDdjMCAxNDUuNDY2IDEwMS4xMTkgMjUwLjYwNCAyNTcuMzU2IDI3NC40MzRDOTk4LjY5OCAzMjUuNTIxIDEwOTQuNjIgNTAxLjQ0MiAxMTQxLjA5IDYyNEg1MTAuNjA2Yy0zMC4wNTktNTkuNDU1LTgyLjczNi0xNzQuNTg4LTE3NS4wNS0yMjAuMjg1eiIgZmlsbD0iI0Y5RkFGQiIvPjwvZz48ZGVmcz48ZmlsdGVyIGlkPSJmaWx0ZXIxX2RkIiB4PSIwIiB5PSIwIiB3aWR0aD0iMTE3My4wOSIgaGVpZ2h0PSI2NTYiIGZpbHRlclVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgY29sb3ItaW50ZXJwb2xhdGlvbi1maWx0ZXJzPSJzUkdCIj48ZmVGbG9vZCBmbG9vZC1vcGFjaXR5PSIwIiByZXN1bHQ9IkJhY2tncm91bmRJbWFnZUZpeCIvPjxmZUNvbG9yTWF0cml4IGluPSJTb3VyY2VBbHBoYSIgdmFsdWVzPSIwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAxMjcgMCIvPjxmZU9mZnNldC8+PGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0iMTYiLz48ZmVDb2xvck1hdHJpeCB2YWx1ZXM9IjAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAuMDIgMCIvPjxmZUJsZW5kIGluMj0iQmFja2dyb3VuZEltYWdlRml4IiByZXN1bHQ9ImVmZmVjdDFfZHJvcFNoYWRvdyIvPjxmZUNvbG9yTWF0cml4IGluPSJTb3VyY2VBbHBoYSIgdmFsdWVzPSIwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAxMjcgMCIvPjxmZU9mZnNldC8+PGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0iMiIvPjxmZUNvbG9yTWF0cml4IHZhbHVlcz0iMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMCAwIDAgMC4wMyAwIi8+PGZlQmxlbmQgaW4yPSJlZmZlY3QxX2Ryb3BTaGFkb3ciIHJlc3VsdD0iZWZmZWN0Ml9kcm9wU2hhZG93Ii8+PGZlQmxlbmQgaW49IlNvdXJjZUdyYXBoaWMiIGluMj0iZWZmZWN0Ml9kcm9wU2hhZG93IiByZXN1bHQ9InNoYXBlIi8+PC9maWx0ZXI+PC9kZWZzPjwvc3ZnPg==');
  background-size: auto 100%;
  background-position: top center;
  background-repeat: no-repeat;

  ${props =>
    props.visible &&
    css`
      opacity: 1;
      transition: opacity 0.3s ease-out;
    `}
`

export default Nav
